import React, { useEffect, useRef, useState } from "react";
import MenuItem from "@mui/material/MenuItem";
import Popover from "@mui/material/Popover";
import { styled } from "@mui/system";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Box from "@mui/material/Box";
import { brandColour } from "@tphglobal/common/theme/style.palette";
import {
  DailyTime,
  DropListItem,
  FieldsPerUnit,
  SetLockedRows,
  UnitLocation,
} from "../../../models";
import {
  getDefaultWeekDay,
  joinDelimitedValues,
  parseDelimitedValues,
} from "../../../utils/commonFunctions";
import { DEFAULT_UNIT_FIELDS } from "../../../utils/constant";
import {
  assignUnitFields,
  handleMealBreakLockedRows,
} from "../../../utils/timeCardFunctions";

interface Props {
  list: DropListItem[];
  fieldKey: keyof DailyTime;
  setWeekData: (update: (weekData: DailyTime[]) => DailyTime[]) => void;
  rowIndex: number;
  value?: string;
  periodEnding?: string;
  enablePortal?: boolean;
  units?: UnitLocation[];
  setMealBreakLockedRows?: SetLockedRows;
  setPayAsLockedRows?: SetLockedRows;
}

const DropList = styled("div")({
  border: "1px solid #ccc",
  background: "white",
  boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
  maxHeight: "200px",
  overflowY: "auto",
  padding: "5px 10px",
});

const StyledMenuItem = styled(MenuItem)({
  padding: "5px 10px",
});

const DropListInput: React.FC<Props> = ({
  list,
  fieldKey,
  setWeekData,
  rowIndex,
  value,
  periodEnding,
  enablePortal = false,
  units,
  setMealBreakLockedRows,
  setPayAsLockedRows,
}) => {
  const [open, setOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<string | null>();
  const anchorRef = useRef<HTMLSpanElement | null>(null);

  const handleClick = () => {
    setOpen((prev) => !prev);
  };

  useEffect(() => {
    if (fieldKey === "Payas") {
      setSelectedItem(value === "0" ? "Select" : value);
    } else {
      setSelectedItem(enablePortal ? value || "Select" : "");
    }
  }, [value, fieldKey, enablePortal]);

  useEffect(() => {
    if (
      list &&
      list.length === 1 &&
      value !== list[0].value &&
      fieldKey === "Locations"
    ) {
      setSelectedItem(list[0].value);
      handleSelectItem(list[0]);
    }
  }, [list, value]);

  const handleSelectItem = (item: DropListItem) => {
    setSelectedItem(item?.value);

    setWeekData((prevWeekData: DailyTime[]) => {
      const newWeekData = [...prevWeekData];
      if (fieldKey === "Payas") {
        if (rowIndex >= 0 && rowIndex < newWeekData.length) {
          let updatedDay = { ...newWeekData[rowIndex] };
          const dayBeforeUpdate = updatedDay?.Day;
          const dateBeforeUpdate = updatedDay?.Date;

          if (item?.value === "Did Not Work") {
            updatedDay = getDefaultWeekDay(periodEnding);

            updatedDay = {
              ...updatedDay,
              [fieldKey]: item.value,
              Day: dayBeforeUpdate,
              Date: dateBeforeUpdate,
              customNote: `${item?.value} `,
            };
          } else {
            updatedDay = {
              ...updatedDay,
              [fieldKey]: item.value || "",
              customNote: `${item?.value} `,
            };
          }

          newWeekData[rowIndex] = updatedDay;
        }
      } else {
        if (rowIndex >= 0 && rowIndex < newWeekData.length) {
          let extraFields: FieldsPerUnit = {};
          let locations = newWeekData[rowIndex].Locations;
          if (fieldKey === "Unit") {
            const selectedUnit = units?.find((u) => u.unit === item.value);
            if (selectedUnit) {
              assignUnitFields(
                extraFields,
                selectedUnit,
                selectedUnit?.customNote || "",
              );
            } else {
              assignUnitFields(
                extraFields,
                DEFAULT_UNIT_FIELDS,
                DEFAULT_UNIT_FIELDS.customNote,
              );
            }

            if (newWeekData[rowIndex].Locations) {
              if (!item.value) {
                locations = null;
              } else {
                // Locations may be a comma-separated string of multiple locations
                const locationList = parseDelimitedValues(locations);

                // Get all valid locations for the selected unit
                const unitLocations =
                  units?.find((u) => u.unit?.includes(item.value || ""))
                    ?.locations || [];
                // Remove locations that do not match the selected unit's locations
                const filteredLocations = locationList.filter((loc) =>
                  unitLocations.includes(loc),
                );
                locations =
                  filteredLocations.length === 0
                    ? null
                    : joinDelimitedValues(filteredLocations);
              }
            }
          }
          if (setMealBreakLockedRows) {
            if (item.value) {
              handleMealBreakLockedRows(
                extraFields,
                rowIndex,
                setMealBreakLockedRows,
              );
            } else {
              setMealBreakLockedRows((prev) => {
                const updated = [...prev];
                updated[rowIndex] = 0;
                return updated;
              });
            }
          }
          setPayAsLockedRows?.((prev) => {
            const updated = [...prev];
            updated[rowIndex] = extraFields.Payas !== "0" ? 1 : 0;
            return updated;
          });

          const updatedDay = {
            ...newWeekData[rowIndex],
            ...extraFields,
            Locations: locations,
            [fieldKey]: item.value,
          };

          newWeekData[rowIndex] = updatedDay;
        }
      }

      return newWeekData;
    });

    setOpen(false);
  };

  const handleClickAway = () => {
    setOpen(false);
  };

  if ((fieldKey === "Unit" || fieldKey === "Locations") && !enablePortal) {
    return null;
  }

  if (enablePortal) {
    return (
      <ClickAwayListener onClickAway={handleClickAway}>
        <Box
          position="relative"
          display="inline-block"
          sx={{
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
            whiteSpace: "normal",
            wordBreak: "break-word",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          <Box
            component="span"
            ref={anchorRef}
            onClick={handleClick}
            sx={{
              padding: "2px 4px",
              cursor: "pointer",
              borderRadius: "4px",
              backgroundColor: open ? brandColour?.primary20 : "white",
            }}
          >
            {selectedItem || fieldKey}
          </Box>

          <Popover
            open={open}
            anchorEl={anchorRef.current}
            onClose={handleClickAway}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            disableScrollLock={true} // keep scrolling behavior unchanged
            PaperProps={{
              sx: {
                mt: "4px",
                p: 0,
                boxSizing: "border-box",
                maxHeight: 220,
                overflow: "visible",
              },
            }}
          >
            <DropList>
              {list?.map((item: DropListItem) => (
                <StyledMenuItem
                  key={item?.value}
                  onClick={() => handleSelectItem(item)}
                >
                  {item?.label}
                </StyledMenuItem>
              ))}
            </DropList>
          </Popover>
        </Box>
      </ClickAwayListener>
    );
  }

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box
        position="relative"
        display="inline-block"
        sx={{
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          whiteSpace: "normal",
          wordBreak: "break-word",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        <Box
          component="span"
          onClick={handleClick}
          sx={{
            padding: "2px 4px",
            cursor: "pointer",
            borderRadius: "4px",
            backgroundColor: open ? brandColour?.primary20 : "white",
            color: enablePortal ? "inherit" : "grey.500",
          }}
        >
          {selectedItem || fieldKey}
        </Box>

        {open && (
          <Box
            display="flex"
            position="absolute"
            top="100%"
            left="0"
            zIndex={100000}
          >
            <DropList>
              {list?.map((item: DropListItem) => (
                <StyledMenuItem
                  key={item?.value}
                  onClick={() => handleSelectItem(item)}
                >
                  {item?.label}
                </StyledMenuItem>
              ))}
            </DropList>
          </Box>
        )}
      </Box>
    </ClickAwayListener>
  );
};

export default DropListInput;
